import React, { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react'
import Menu from '../../wui-menu/src'
import Divider from '../../wui-divider/src'
import { MenuProps } from '../../wui-menu/src/iMenu';
import { Key } from './iTable';
import { getLangInfo } from '../../wui-locale/src/tool';
import i18n from './lib/i18n';

const { Item } = Menu

interface PopMenuProps {
    clsPrefix?: string;
    popMenu?: (rowKeys: Key[], colKeys?: Key[]) => {key: Key, text: Key}[];
    onPopMenuIsShow?: (open: boolean) => void;
    onMenuClick?: (type: Key, rowKeys: Key[], colKeys: Key[]) => void;
	onCopyCell?: (e: any, type: string) => void;
	locale?: any;
	dir?: 'ltr' | 'rtl';
	hideDefaultMenu?: boolean;
}

export interface PopMenuRef {
    show: (event: React.MouseEvent, contextMenuKey: Key[], contextMenuDataIndex: Key[], showX: number, showY: number) => void;
    hide: () => void;
}

const PopMenu = forwardRef<PopMenuRef, PopMenuProps>((props, ref) => {
    const {
        clsPrefix,
        popMenu,
        onPopMenuIsShow,
        onMenuClick,
        onCopyCell,
        locale,
        dir: direction,
        hideDefaultMenu = false
    } = props;

    const [visible, setVisible] = useState(false);
    const [contextMenuKey, setContextMenuKey] = useState<Key[]>([]);
    const [contextMenuDataIndex, setContextMenuDataIndex] = useState<Key[]>([]);
    const [position, setPosition] = useState<any>({});

    const popMenuRef = useRef<HTMLDivElement>(null);
    const mountedRef = useRef(false);

    const contains = useCallback((root: any, n: any) => {
        let node: any = n;
        while (node) {
            if (node === root) return true;
            node = node.parentNode
        }
        return false;
    }, []);

    const hide = useCallback(() => {
        if (visible) {
            setVisible(false);
        }
        onPopMenuIsShow && onPopMenuIsShow(false);
    }, [visible, onPopMenuIsShow]);

    const onDocumentClick = useCallback((event: any) => {
        if (mountedRef.current === false) return;
        const target = event.target;
        const contextDom = popMenuRef.current!;
        if (!contains(contextDom, target)) {
            hide();
        }
    }, [contains, hide]);

    const show = useCallback((event: React.MouseEvent, contextMenuKeyParam: Key[], contextMenuDataIndexParam: Key[], showX: number, showY: number) => {
        const positionObj = {
            left: showX !== undefined ? showX : event.clientX,
            top: (showY !== undefined ? showY : event.clientY),
            position: 'fixed',
            zIndex: '1400',
        };
        setVisible(true);
        setContextMenuKey(contextMenuKeyParam);
        setContextMenuDataIndex(contextMenuDataIndexParam);
        setPosition(positionObj);
        onPopMenuIsShow && onPopMenuIsShow(true);
    }, [onPopMenuIsShow]);

    useImperativeHandle(ref, () => ({
        show,
        hide
    }), [show, hide]);

    useEffect(() => {
        mountedRef.current = true;
        document.addEventListener('click', onDocumentClick);
        return () => {
            mountedRef.current = false;
            document.removeEventListener('click', onDocumentClick);
        };
    }, [onDocumentClick]);

    const onSelect: MenuProps['onSelect'] = useCallback((val: any) => {
        const { key, domEvent } = val;
        if (key === '_copyText') {
            onCopyCell && onCopyCell(domEvent, '_all');
        } else if (key === '_copyRows') {
            onCopyCell && onCopyCell(domEvent, '_row');
        } else if (key === '_copyCells') {
            onCopyCell && onCopyCell(domEvent, '_cell');
        } else {
            onMenuClick && onMenuClick!(val?.key, contextMenuKey, contextMenuDataIndex)
        }
        hide();
    }, [onCopyCell, onMenuClick, contextMenuKey, contextMenuDataIndex, hide]);

    const renderMenus = useCallback(() => {
        if (!popMenu) return;
        const _locale = getLangInfo(locale, i18n, 'table');
        const items = popMenu(contextMenuKey, contextMenuDataIndex);
        const defaultItems = [
            {
                key: '_copyText',
                text: `${_locale.langMap.copyText || '复制文本'}`
            },
            {
                key: '_copyRows',
                text: `${_locale.langMap.copyRows || '复制整行'}`
            },
            {
                key: '_copyCells',
                text: `${_locale.langMap.copyCols || '复制整列'}`
            }
        ];
        const defaultMenuItems = defaultItems.map((item: any) => {
            return <Item key={item.key}>{item.text}</Item>
        });

        const customMenuItems = Array.isArray(items) && items.length ? items.map((item: any) => {
            return <Item key={item.key}>{item.text}</Item>
        }) : null;

        const dividerLine = Array.isArray(items) && items.length ? <Divider style={{ margin: 0 }}/> : null;

        return (
            <div className={`${clsPrefix}-cell-selected-menu`}>
                {!hideDefaultMenu && <Menu onSelect={onSelect} mode={'dropdown'} className={`${clsPrefix}-close-menu`} dir={direction}>{defaultMenuItems}</Menu>}
                {!hideDefaultMenu && dividerLine}
                { customMenuItems ? <Menu onSelect={onSelect} mode={'dropdown'} className={`${clsPrefix}-close-menu`} dir={direction}>{customMenuItems}</Menu> : null }
            </div>
        )
    }, [popMenu, locale, contextMenuKey, contextMenuDataIndex, clsPrefix, hideDefaultMenu, onSelect, direction]);

    return visible ? (
        <div style={position} ref={popMenuRef}>
            {renderMenus()}
        </div>
    ) : null;
});

PopMenu.displayName = 'PopMenu';

export default PopMenu
